import React, { useRef, useState, useEffect, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import classes from "./AudioWaveformPlayer.scss";
import { allowURLWithCredentials } from "Utils/SecureUrlHelpers.js";
import classNames from "classnames";
import {
  Button,
  Dropdown,
  DropdownMenu,
  IconButton,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  PlayFilled,
  PauseFilled,
  DownloadOutlined,
} from "@toddle-edu/ds-icons";

import WaveSurfer from "wavesurfer.js";
import moment from "moment";

const KEYBOARD_AUDIO_JUMP_SECONDS = 5;

const PlayerButton = props => {
  const {
    handleClick,
    mediaType,
    playing,
    isUploading,
    onDownload,
    testId,
    buttonSize,
  } = props;
  // const playerButtonClass = classNames(classes.playerButton, {
  //   [classes.secondaryPlayerButton]: mediaType === "secondary",
  //   [classes.audioUploading]: isUploading,
  // });
  return (
    <Tooltip tooltip={playing ? "Pause" : "Play"}>
      <IconButton
        icon={playing ? <PauseFilled /> : <PlayFilled />}
        size={buttonSize}
        variant="neutral"
        type="plain"
        aria-label={playing ? "Pause recording" : "Play recording"}
        onClick={e => {
          e.stopPropagation();
          handleClick();
        }}
        dsVersion="2.0"
        disabled={isUploading}
        testId={`${testId}-audioWaveformPlayerV2-togglePlayPause`}
      />
    </Tooltip>
  );
};

const roundRectIsSupported = () => {
  const canvasElement = document.createElement("canvas");
  const context = canvasElement.getContext("2d");

  if (context && typeof context.roundRect === "function") return true;
  return false;
};

/**
 * Custom wavesurfer hook
 */
const useWavesurfer = (waveFormContainerRef, blob) => {
  const [wavesurfer, setWavesurfer] = useState(null);

  useEffect(() => {
    if (!waveFormContainerRef.current) return;

    if (!blob) return;

    const wavesurferInstance = WaveSurfer.create({
      barGap: 2,
      barRadius: roundRectIsSupported() ? 2 : 0,
      barWidth: 2,
      cursorColor: "#FAFAFA",
      cursorWidth: 8,
      fetchParams: {
        credentials: allowURLWithCredentials(blob.url)
          ? "include"
          : "same-origin",
      },
      fillParent: true,
      height: 24,
      progressColor: "#CCCCCC",
      url: blob.url,
      waveColor: "#525252",
      container: waveFormContainerRef.current,
    });

    setWavesurfer(wavesurferInstance);

    return () => {
      wavesurferInstance?.destroy();
    };
  }, [blob, waveFormContainerRef]);

  return wavesurfer;
};

/**
 * Custom hook to track current playback time for accessibility
 */
const useCurrentTime = wavesurfer => {
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (!wavesurfer) return;

    const handleTimeUpdate = currentTime => {
      setCurrentTime(Math.floor(currentTime));
    };

    const unsubscribe = wavesurfer.on("timeupdate", handleTimeUpdate);

    return () => {
      unsubscribe();
    };
  }, [wavesurfer]);

  return currentTime;
};

const AudioWaveformPlayerV2 = React.forwardRef((props, ref) => {
  const {
    attachment,
    audioLoaded,
    audioPlaybackTime = 0,
    mediaType,
    isUploading,
    onAudioDownload,
    pausedRecordingState,
    testId,
    shouldBeResponsive,
  } = props;

  const [duration, setDuration] = useState("00:00");
  const [playing, setPlaying] = useState(false);
  const [audioSpeed, setAudioSpeed] = useState("1");

  const waveFormContainerRef = useRef(null);
  const wavesurfer = useWavesurfer(waveFormContainerRef, attachment);
  const currentTime = useCurrentTime(wavesurfer);
  useImperativeHandle(ref, () => ({
    /**
     * Toggles the audio playback state
     */
    toggleAudio: () => {
      wavesurfer?.playPause();
    },
    /**
     * Sets the audio time at which the audio should start playing
     * @returns {number} Returns the current time of the audio in seconds
     */
    getCurrentTime: () => {
      return wavesurfer?.getCurrentTime();
    },
  }));

  // audio speed options
  const audioSpeedOptions = [
    {
      key: "dropdown_title",
      label: "Playback speed",
      isItemGroup: true,
      options: [
        {
          key: "0.25",
          label: "0.25x",
        },
        {
          key: "0.5",
          label: "0.5x",
        },
        {
          key: "0.75",
          label: "0.75x",
        },
        {
          key: "1",
          label: "1x",
        },
        {
          key: "1.5",
          label: "1.5x",
        },
        {
          key: "2",
          label: "2x",
        },
      ],
    },
  ];

  useEffect(() => {
    if (!wavesurfer) return;

    setPlaying(false);
    setDuration("00:00");
    setAudioSpeed("1");

    // seek to a specific time if prop provided
    wavesurfer.seekTo(audioPlaybackTime);

    const subscriptions = [
      // subscribe to when waveform is mounted and ready event
      wavesurfer.on("ready", duration => {
        const totalDurationInSeconds = ~~duration;
        const currentDuration = moment
          .duration(totalDurationInSeconds, "seconds")
          .format("mm:ss", { trim: false });

        audioLoaded();

        setDuration(prevDuration => {
          return currentDuration !== prevDuration && currentDuration !== "00:00"
            ? currentDuration
            : prevDuration;
        });
      }),

      //subscribe to play and pause events
      wavesurfer.on("play", () => {
        setPlaying(true);
      }),
      wavesurfer.on("pause", () => {
        setPlaying(false);
      }),

      wavesurfer.on("finish", () => {
        wavesurfer.setTime(0);
      }),
    ];

    return () => {
      subscriptions.forEach(unsubscribe => unsubscribe());
    };
  }, [wavesurfer, audioPlaybackTime]);

  const changeAudioSpeed = e => {
    setAudioSpeed(e.key);
    wavesurfer.setPlaybackRate(e.key);
  };

  const durationContainerClasses = classNames(
    classes.durationContainer,
    "text-label"
  );

  const waveformContainerClasses = classNames(classes.waveformContainer, {
    [classes.waveformContainerResponsive]: shouldBeResponsive,
  });

  const audioSpeedControlClasses = classNames(classes.audioSpeedControl, {
    [classes.audioSpeedControlResponsive]: shouldBeResponsive,
  });

  const buttonSize =
    shouldBeResponsive && window.innerWidth < 470 ? "small" : "medium";

  const togglePlayPause = () => {
    if (playing) {
      wavesurfer.pause();
      return;
    }
    wavesurfer.play();
  };

  const formatTime = seconds => {
    return moment.duration(seconds, "seconds").format("mm:ss", { trim: false });
  };

  const handleWaveformKeyDown = e => {
    e.stopPropagation();

    if (!wavesurfer) return;

    const currentTime = wavesurfer.getCurrentTime();

    switch (e.key) {
      case "ArrowRight":
        wavesurfer.setTime(currentTime + KEYBOARD_AUDIO_JUMP_SECONDS);
        if (playing) {
          wavesurfer.play();
        }
        break;
      case "ArrowLeft":
        wavesurfer.setTime(
          Math.max(0, currentTime - KEYBOARD_AUDIO_JUMP_SECONDS)
        );
        if (playing) {
          wavesurfer.play();
        }
        break;
      case " ":
        e.preventDefault();
        togglePlayPause();
        break;
      case "Enter":
        togglePlayPause();
        break;
      default:
        break;
    }
  };

  return (
    <div className={classes.visualContainer}>
      <div className={classes.buttonContainer}>
        <PlayerButton
          playing={playing}
          handleClick={() => wavesurfer.playPause()}
          mediaType={mediaType}
          isUploading={isUploading}
          testId={testId}
          buttonSize={buttonSize}
        />
      </div>
      <div
        tabIndex={0}
        role="slider"
        aria-live="polite"
        aria-valuemin={0}
        aria-valuemax={wavesurfer ? Math.floor(wavesurfer.getDuration()) : 0}
        aria-valuenow={currentTime}
        aria-valuetext={`${formatTime(currentTime)} of ${duration}`}
        id="waveform"
        aria-label="Audio seeker, use left and right arrow keys to seek backward and forward"
        className={waveformContainerClasses}
        ref={waveFormContainerRef}
        onClick={e => e.stopPropagation()}
        onKeyDown={handleWaveformKeyDown}
        onMouseDown={e => e.stopPropagation()}
        data-test-id={`${testId}-audioWaveformPlayerV2-seeker`}
      />
      <div
        className={durationContainerClasses}
        onClick={e => e.stopPropagation()}
        role="presentation"
      >
        {duration}
      </div>
      {onAudioDownload !== null && (
        <Tooltip tooltip="Download">
          <IconButton
            icon={<DownloadOutlined />}
            size={buttonSize}
            variant="neutral"
            type="outlined"
            aria-label="Download recorded audio"
            dsVersion="2.0"
            onClick={onAudioDownload}
            testId={`${testId}-audioWaveformPlayerV2-audioDownload`}
          />
        </Tooltip>
      )}
      {!pausedRecordingState && (
        <div
          className={audioSpeedControlClasses}
          // to prevent audio from playing when clicking on the dropdown
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          role="presentation"
        >
          <Dropdown
            getPopupContainer={() => document.body}
            overlay={
              <DropdownMenu
                showSelection={true}
                value={audioSpeed}
                onClick={changeAudioSpeed}
                options={audioSpeedOptions}
                dsVersion="2.0"
                testId={`${testId}-audioWaveformPlayerV2-audioSpeeedControl`}
              />
            }
          >
            <Button
              className={classes.playbackSpeedTrigger}
              variant="neutral"
              type="outlined"
              dsVersion="2.0"
              size={buttonSize}
              testId={`${testId}-audioWaveformPlayerV2-audioSpeedControl`}
              aria-label={`Change playback speed, current speed is ${audioSpeed}x`}
            >
              {`${audioSpeed}x`}
            </Button>
          </Dropdown>
        </div>
      )}
    </div>
  );
});

AudioWaveformPlayerV2.defaultProps = {
  mediaType: "primary",
  isUploading: false,
  shouldBeResponsive: false,
};

AudioWaveformPlayerV2.propTypes = {
  shouldBeResponsive: PropTypes.bool,
};

AudioWaveformPlayerV2.displayName = "AudioWaveformPlayerV2";

export { AudioWaveformPlayerV2 };
